import React, { useState, useEffect } from 'react';
import { submitBug, uploadAttachments, getCurrentUser } from '../api';
import './SubmitBug.css';

function SubmitBug() {
    const [formData, setFormData] = useState({
        phone_model: '',
        system_version: '',
        software: '',
        bug_description: '',
        steps_to_reproduce: '',
        email: '',
        name: '',
        security_version: '',
        kaios_api_level: ''
    });
    const [files, setFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [submittedBugId, setSubmittedBugId] = useState('');
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [productLocked, setProductLocked] = useState(false);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        const loadUserData = async () => {
            const userToken = localStorage.getItem('userToken');
            const userEmail = localStorage.getItem('userEmail');
            
            if (userToken && userEmail) {
                try {
                    const userData = await getCurrentUser();
                    console.log('User data loaded:', userData);
                    setIsLoggedIn(true);
                    
                    // Auto-populate email and name
                    setFormData(prev => ({
                        ...prev,
                        email: userData.email,
                        name: userData.name || ''
                    }));
                    
                    // Auto-populate product info if user has a product assigned
                    if (userData.product && userData.product.phone_model) {
                        console.log('Auto-populating product info:', userData.product);
                        setFormData(prev => ({
                            ...prev,
                            phone_model: userData.product.phone_model,
                            system_version: userData.product.system_version
                        }));
                        setProductLocked(true);
                    } else {
                        console.log('No product assigned to user or product data missing');
                    }
                } catch (error) {
                    console.error('Failed to load user data:', error);
                }
            }
        };
        
        loadUserData();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        setFiles(prev => [...prev, ...newFiles]);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const droppedFiles = Array.from(e.dataTransfer.files);
        if (droppedFiles.length > 0) {
            setFiles(prev => [...prev, ...droppedFiles]);
        }
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError('');

        try {
            const result = await submitBug(formData);
            setSubmittedBugId(result.id);
            
            // Upload attachments if any
            if (files.length > 0) {
                try {
                    await uploadAttachments(result.id, files);
                } catch (uploadError) {
                    console.error('Failed to upload attachments:', uploadError);
                }
            }
            
            setSubmitSuccess(true);
            
            // Save bug ID to localStorage for guest tracking
            const savedBugIds = JSON.parse(localStorage.getItem('submittedBugIds') || '[]');
            if (!savedBugIds.includes(result.id)) {
                savedBugIds.push(result.id);
                localStorage.setItem('submittedBugIds', JSON.stringify(savedBugIds));
            }
        } catch (error) {
            setSubmitError(error.response?.data?.detail || 'Failed to submit bug report. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };


    const resetForm = () => {
        setFormData({
            phone_model: '',
            system_version: '',
            software: '',
            bug_description: '',
            steps_to_reproduce: '',
            email: '',
            name: '',
            security_version: '',
            kaios_api_level: ''
        });
        setFiles([]);
        setSubmitSuccess(false);
        setSubmitError('');
        setSubmittedBugId('');
    };

    if (submitSuccess) {
        return (
            <div className="submit-bug-page">
                <div className="hero-section">
                    <h1 className="hero-title">Security Bug<br />Feedback</h1>
                    <p className="hero-subtitle">Help us improve KaiOS by reporting bugs and issues you encounter</p>
                    <div className="hero-decoration"></div>
                </div>

                <div className="form-container">
                    <div className="success-message">
                        <div className="success-icon">✓</div>
                        <h3>Thank you for your feedback!</h3>
                        <p>Your bug report has been submitted successfully.</p>
                        <p className="bug-id">Bug ID: <strong>{submittedBugId}</strong></p>
                        <p>We'll review it and get back to you soon.</p>
                        <button className="btn btn-primary" onClick={resetForm}>
                            Submit Another Report
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="submit-bug-page">
            <div className="hero-section">
                <h1 className="hero-title">Security Bug<br />Feedback</h1>
                <p className="hero-subtitle">Help us improve KaiOS by reporting bugs and issues you encounter</p>
                <div className="hero-decoration"></div>
            </div>

            <div className="form-container">
                <form onSubmit={handleSubmit} className="feedback-form">
                    {submitError && (
                        <div className="error-banner">
                            {submitError}
                        </div>
                    )}

                    <div className="form-section">
                        <h3 className="section-title">Device Information</h3>
                        
                        <div className="form-group">
                            <label htmlFor="phone_model" className="form-label">
                                Phone Model <span className="required">*</span>
                                <span className="info-tooltip-wrap">
                                    <span className="info-icon">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="12" y1="16" x2="12" y2="12"></line>
                                            <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                        </svg>
                                    </span>
                                    <span className="tooltip-popup">
                                        You can find the Phone Model in <strong>Settings &gt; Device &gt; Device Information</strong>
                                    </span>
                                </span>
                            </label>
                            <input
                                type="text"
                                id="phone_model"
                                name="phone_model"
                                className="form-input"
                                placeholder="e.g., WM40 4G"
                                value={formData.phone_model}
                                onChange={handleChange}
                                disabled={productLocked}
                                required
                                maxLength={20}
                            />
                            <span className="field-hint">Max 20 characters ({formData.phone_model.length}/20)</span>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="system_version" className="form-label">
                                    System Version <span className="required">*</span>
                                    <span className="info-tooltip-wrap">
                                        <span className="info-icon">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                            </svg>
                                        </span>
                                        <span className="tooltip-popup">
                                            You can find the System Version in <strong>Settings &gt; Device &gt; Device Information</strong>
                                        </span>
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    id="system_version"
                                    name="system_version"
                                    className="form-input"
                                    placeholder="e.g., KaiOS 2.6"
                                    value={formData.system_version}
                                    onChange={handleChange}
                                    disabled={productLocked}
                                    required
                                    maxLength={20}
                                />
                                <span className="field-hint">Max 20 characters ({formData.system_version.length}/20)</span>
                            </div>

                            <div className="form-group">
                                <label htmlFor="software" className="form-label">
                                    App <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="software"
                                    name="software"
                                    className="form-input"
                                    placeholder="e.g., Browser"
                                    value={formData.software}
                                    onChange={handleChange}
                                    required
                                    maxLength={20}
                                />
                                <span className="field-hint">Max 20 characters ({formData.software.length}/20)</span>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="security_version" className="form-label">
                                    Security Version
                                    <span className="info-tooltip-wrap">
                                        <span className="info-icon">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                            </svg>
                                        </span>
                                        <span className="tooltip-popup">
                                            Available on KaiOS 2.6+. Find in <strong>Settings &gt; Device &gt; Device Information</strong>
                                        </span>
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    id="security_version"
                                    name="security_version"
                                    className="form-input"
                                    placeholder="e.g., 20251031"
                                    value={formData.security_version}
                                    onChange={handleChange}
                                    maxLength={20}
                                />
                                <span className="field-hint">Max 20 characters ({formData.security_version.length}/20)</span>
                            </div>

                            <div className="form-group">
                                <label htmlFor="kaios_api_level" className="form-label">
                                    KaiOS API Level
                                    <span className="info-tooltip-wrap">
                                        <span className="info-icon">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                            </svg>
                                        </span>
                                        <span className="tooltip-popup">
                                            Available on KaiOS 2.6+. Find in <strong>Settings &gt; Device &gt; Device Information</strong>
                                        </span>
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    id="kaios_api_level"
                                    name="kaios_api_level"
                                    className="form-input"
                                    placeholder="e.g., 26"
                                    value={formData.kaios_api_level}
                                    onChange={handleChange}
                                    maxLength={20}
                                />
                                <span className="field-hint">Max 20 characters ({formData.kaios_api_level.length}/20)</span>
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3 className="section-title">Bug Details</h3>
                        
                        <div className="form-group">
                            <label htmlFor="bug_description" className="form-label">
                                Problem Description <span className="required">*</span>
                            </label>
                            <textarea
                                id="bug_description"
                                name="bug_description"
                                className="form-textarea"
                                rows="5"
                                placeholder="Please describe the bug you encountered in detail..."
                                value={formData.bug_description}
                                onChange={handleChange}
                                required
                                maxLength={5000}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="steps_to_reproduce" className="form-label">
                                Steps to Reproduce
                            </label>
                            <textarea
                                id="steps_to_reproduce"
                                name="steps_to_reproduce"
                                className="form-textarea"
                                rows="4"
                                placeholder="1. Open the app&#10;2. Click on...&#10;3. ..."
                                value={formData.steps_to_reproduce}
                                onChange={handleChange}
                                maxLength={5000}
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">Attachments (Screenshots/Logs)</label>
                            <span className="field-hint">Max 10MB per file, up to 10 files</span>
                            <div
                                className={`file-upload-wrapper ${isDragging ? 'dragging' : ''}`}
                                onDragOver={handleDragOver}
                                onDragEnter={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                            >
                                <input
                                    type="file"
                                    id="fileUpload"
                                    className="file-input"
                                    multiple
                                    accept="image/*,.log,.txt"
                                    onChange={handleFileChange}
                                />
                                <label htmlFor="fileUpload" className="file-upload-label">
                                    <svg className="upload-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                        <polyline points="17 8 12 3 7 8"></polyline>
                                        <line x1="12" y1="3" x2="12" y2="15"></line>
                                    </svg>
                                    <span>Choose files or drag here</span>
                                </label>
                                {files.length > 0 && (
                                    <div className="file-list">
                                        {files.map((file, index) => (
                                            <div key={index} className="file-item">
                                                <div className="file-item-name">
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                                                        <polyline points="13 2 13 9 20 9"></polyline>
                                                    </svg>
                                                    <span>{file.name}</span>
                                                </div>
                                                <span className="file-item-size">{formatFileSize(file.size)}</span>
                                                <button
                                                    type="button"
                                                    className="file-item-remove"
                                                    onClick={() => removeFile(index)}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="form-section">
                        <h3 className="section-title">Contact Information</h3>
                        
                        <div className="form-group">
                            <label htmlFor="email" className="form-label">
                                Email Address <span className="required">*</span>
                            </label>
                            <input
                                type="email"
                                id="email"
                                name="email"
                                className="form-input"
                                placeholder="your.email@example.com"
                                value={formData.email}
                                onChange={handleChange}
                                required
                                maxLength={254}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="name" className="form-label">
                                Name (Optional)
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                className="form-input"
                                placeholder="Your name"
                                value={formData.name}
                                onChange={handleChange}
                                maxLength={200}
                            />
                        </div>
                    </div>

                    <div className="form-actions">
                        <button 
                            type="submit" 
                            className="btn btn-primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-secondary"
                            onClick={resetForm}
                        >
                            Clear Form
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default SubmitBug;
